import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useListAdminsQuery } from '../../features/admin/adminAccountsApi';
import { useListAdminInvestorsQuery } from '../../features/investors/adminInvestorsApi';
import { Skeleton } from '../ui/Skeleton';
import Badge from '../ui/Badge';

/**
 * Type-to-search picker for the Audit Logs `causer_uuid` filter — a causer
 * can be any user (admin or investor; the log spans KYC/support/plan/admin
 * activity), so this combines the two existing real search sources this app
 * already has rather than inventing a new directory endpoint:
 *   - GET /admin/admins (small, unpaginated — same source AdminEmailAutocomplete
 *     uses, filtered client-side by name/email/uuid)
 *   - GET /admin/investors?search= (server-side search, only fired once the
 *     query is 2+ chars, since the investor list can be large)
 * Selecting a result emits that account's uuid via onChange; typing without
 * picking a match clears the committed value.
 */
export default function CauserAutocomplete({ value, onChange, label = 'Caused by', placeholder = 'Search admins or investors by name or email…' }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const { data: adminsData, isLoading: adminsLoading } = useListAdminsQuery();
  const admins = adminsData ?? [];

  const trimmed = query.trim();
  const { data: investorsData, isFetching: investorsLoading } = useListAdminInvestorsQuery(
    { search: trimmed, per_page: 8 },
    { skip: trimmed.length < 2 }
  );
  const investors = investorsData?.investors ?? [];

  useEffect(() => {
    if (!value) {
      setQuery('');
      return;
    }
    const match = admins.find((a) => a.id === value) || investors.find((i) => i.id === value);
    if (match) setQuery(`${match.full_name} — ${match.email}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const q = trimmed.toLowerCase();
  const matchedAdmins = q
    ? admins.filter((a) => a.email.toLowerCase().includes(q) || a.full_name.toLowerCase().includes(q) || (a.id && a.id.toLowerCase().includes(q)))
    : admins.slice(0, 8);

  const select = (person) => {
    onChange(person.id);
    setQuery(`${person.full_name} — ${person.email}`);
    setOpen(false);
  };

  const handleTyping = (e) => {
    setQuery(e.target.value);
    onChange('');
    setOpen(true);
  };

  const isLoading = adminsLoading || investorsLoading;
  const hasResults = matchedAdmins.length > 0 || investors.length > 0;

  return (
    <div className="relative" ref={containerRef}>
      {label && <label className="block text-sm font-medium text-foreground-muted mb-1.5">{label}</label>}
      <input
        type="text"
        value={query}
        onChange={handleTyping}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        autoComplete="off"
        className={clsx(
          'w-full rounded-xl bg-background-elevated border border-border px-3.5 py-2.5 text-sm text-foreground',
          'placeholder:text-foreground-subtle transition-colors',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus:border-border-strong'
        )}
      />

      {open && (
        <div className="absolute left-0 right-0 top-full mt-1.5 max-h-72 overflow-y-auto rounded-xl border border-border bg-background-surface shadow-card-hover z-30 py-1">
          {isLoading && (
            <div className="px-3 py-2 space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-8 w-full" />
              ))}
            </div>
          )}

          {!isLoading && !hasResults && (
            <p className="px-3 py-3 text-sm text-foreground-muted text-center">
              {trimmed.length < 2 ? 'Type at least 2 characters to search investors.' : 'No matching accounts.'}
            </p>
          )}

          {!isLoading && matchedAdmins.length > 0 && (
            <div>
              <p className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-foreground-subtle">Admins</p>
              {matchedAdmins.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => select(a)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-background-elevated transition-colors"
                >
                  <span className="min-w-0">
                    <span className="block text-sm text-foreground truncate">{a.full_name}</span>
                    <span className="block text-xs text-foreground-subtle truncate">{a.email}</span>
                  </span>
                  <Badge tone="neutral" className="!px-1.5 !py-0.5 text-[10px] shrink-0">
                    Admin
                  </Badge>
                </button>
              ))}
            </div>
          )}

          {!isLoading && investors.length > 0 && (
            <div>
              <p className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-foreground-subtle">Investors</p>
              {investors.map((i) => (
                <button
                  key={i.id}
                  type="button"
                  onClick={() => select(i)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-background-elevated transition-colors"
                >
                  <span className="min-w-0">
                    <span className="block text-sm text-foreground truncate">{i.full_name}</span>
                    <span className="block text-xs text-foreground-subtle truncate">{i.email}</span>
                  </span>
                  <Badge tone="info" className="!px-1.5 !py-0.5 text-[10px] shrink-0">
                    Investor
                  </Badge>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
